import { Injectable } from '@angular/core';
import { IDEAApiService, IDEATranslationsService } from '@idea-ionic/common';

import { LegalDocument, LegalDocuments, getLegalDocumentsVersions } from '@models/legalDocument.model';
import { LegalDocumentsVersions } from '@models/user.model';

/**
 * For each legal document of the platform, the sections of its default content (keys in the translations),
 * in order of display.
 */
const DEFAULT_CONTENT_SECTIONS: { [document in LegalDocuments]?: string[] } = {
  [LegalDocuments.PRIVACY_POLICY]: [
    'DISCLAIMER',
    'CONTROLLER',
    'DATA_COLLECTED',
    'PURPOSES_AND_LEGAL_BASES',
    'SPECIAL_CATEGORIES',
    'RECIPIENTS',
    'INTERNATIONAL_TRANSFER',
    'RETAINING_DATA',
    'SECURITY_DATA',
    'RIGHTS',
    'COOKIES',
    'THIRD_PARTY_WEBSITES',
    'AMENDMENTS'
  ].map(section => 'PRIVACY_POLICY.'.concat(section)),
  [LegalDocuments.TERMS]: ['INTRO', 'SERVICE', 'RIGHTS', 'LIABILITY', 'LAW_AND_MISCELLANEOUS'].map(section =>
    'TERMS.'.concat(section)
  )
};

/**
 * For each legal document of the event, the PDF (bundled with the app) to show until the admins upload one.
 */
const DEFAULT_PDF_URLS: { [document in LegalDocuments]?: string } = {
  [LegalDocuments.EVENT_TERMS]: '/assets/docs/egm-25-tc.pdf',
  [LegalDocuments.CODE_OF_CONDUCT]: '/assets/docs/egm-24-coc.pdf'
};

@Injectable({ providedIn: 'root' })
export class LegalDocumentsService {
  constructor(
    private api: IDEAApiService,
    private t: IDEATranslationsService
  ) {}

  /**
   * Get the current versions of the legal documents. It doesn't require the user to be signed-in.
   */
  async getCurrentVersions(): Promise<LegalDocumentsVersions> {
    const documents: LegalDocument[] = await this.api.getResource('legal-documents');
    return getLegalDocumentsVersions(documents.map(x => new LegalDocument(x)));
  }

  /**
   * Get a legal document. It doesn't require the user to be signed-in.
   * If the admins have yet to edit the document, the content is the default one (from the translations), if any.
   */
  async getById(documentId: LegalDocuments): Promise<LegalDocument> {
    const legalDocument = new LegalDocument(await this.api.getResource(['legal-documents', documentId]));
    if (!legalDocument.content) legalDocument.content = this.getDefaultContent(documentId);
    return legalDocument;
  }
  /**
   * Get the default content (HTML) of a legal document, from the translations.
   */
  getDefaultContent(documentId: LegalDocuments): string {
    return (DEFAULT_CONTENT_SECTIONS[documentId] ?? []).map(section => `<div>${this.t._(section)}</div>`).join('\n');
  }
  /**
   * Get the URL to the PDF of a legal document, if any: the one uploaded by the admins or else the default one.
   * Note: the URL of an uploaded PDF is temporary.
   */
  getPDFURL(legalDocument: LegalDocument): string | null {
    return legalDocument.pdfURL ?? DEFAULT_PDF_URLS[legalDocument.documentId] ?? null;
  }

  /**
   * Upload a new PDF for a legal document and get its ID; the document uses the PDF once it's saved with that ID.
   */
  async uploadPDF(documentId: LegalDocuments, file: File): Promise<string> {
    const body = { action: 'GET_PDF_UPLOAD_URL' };
    const { url, id } = await this.api.patchResource(['legal-documents', documentId], { body });
    const res = await fetch(url, { method: 'PUT', body: file, headers: { 'Content-Type': 'application/pdf' } });
    if (!res.ok) throw new Error('Upload failed');
    return id;
  }

  /**
   * Update the content of a legal document, optionally asking the users to accept it again (new version).
   */
  async update(legalDocument: LegalDocument, options: { askUsersToAcceptAgain: boolean }): Promise<LegalDocument> {
    const body = { ...legalDocument, askUsersToAcceptAgain: options.askUsersToAcceptAgain };
    return new LegalDocument(await this.api.putResource(['legal-documents', legalDocument.documentId], { body }));
  }
}
